import { SxProps } from '@mui/material';
import { useSelector } from 'react-redux';
import {
    Booking,
    BookingCalendarSelection,
    CredentialsType,
    DateRange,
    DateRangeWithOffset,
    Device,
    DeviceForCredentials,
    DevicesResponse,
    DevicesType,
    DutBookingCredentials,
    SlotIndex,
    TrafficGeneratorBookingCredentials,
    WeekRanges,
} from '@pages/booking/types';
import i18n from '@app/locale/i18n';
import { selectSelectedBookings } from '@pages/booking/store/selectors';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { FUTURE_WEEK_COUNT, PAST_WEEK_COUNT } from '@pages/booking/constants';

export function getWeekRangesAroundToday(): WeekRanges {
    const normalizeToMonday = (date: Date): Date => {
        const d = new Date(date);
        const day = d.getDay() || 7; // 1–7, where 7 is sunday
        d.setDate(d.getDate() - (day - 1));
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const createRange = (baseMonday: Date, offset: number): DateRangeWithOffset => {
        const start = new Date(baseMonday);
        start.setDate(start.getDate() + offset * 7);

        const end = new Date(start);
        end.setDate(start.getDate() + 6);

        return { startDate: start, endDate: end, offset };
    };

    const todayMonday = normalizeToMonday(new Date());
    const list: DateRangeWithOffset[] = [];
    const rangesByOffset: Record<number, DateRangeWithOffset> = {};

    const add = (offset: number) => {
        const range = createRange(todayMonday, offset);
        list.push(range);
        rangesByOffset[offset] = range;
    };

    // past weeks
    for (let offset = -PAST_WEEK_COUNT; offset < 0; offset++) {
        add(offset);
    }

    // current week
    add(0);

    // future weeks
    for (let offset = 1; offset <= FUTURE_WEEK_COUNT; offset++) {
        add(offset);
    }

    return { list, rangesByOffset };
}

export function findWeekRangeForBooking(booking: Booking, weekRanges: WeekRanges): DateRangeWithOffset | null {
    const from = new Date(booking.booked_from);
    const to = new Date(booking.booked_to);

    for (const range of weekRanges.list) {
        const { startDate, endDate } = range;

        if (from <= endDate && to >= startDate) {
            return range;
        }
    }

    return null;
}

export function getBookingForItem(itemDateTime: Date, bookings: Booking[], slotIndex?: SlotIndex): Booking | null {
    const itemDateTimeDateObject = new Date(itemDateTime);

    return (
        bookings.find((booking) => {
            const suitableDate =
                itemDateTimeDateObject >= new Date(booking.booked_from) &&
                itemDateTimeDateObject < new Date(booking.booked_to);
            return slotIndex !== undefined ? suitableDate && booking.slots?.includes(slotIndex) : suitableDate;
        }) || null
    );
}

export function generateDatesFromRange(range: DateRange): Date[] {
    const dates: Date[] = [];
    const currentDate = new Date(range.startDate);

    while (currentDate <= range.endDate) {
        dates.push(new Date(currentDate)); // Push a copy to avoid mutation
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
}

export function isPastCalendarItem(date: Date | string, strictMatch = true): boolean {
    const now = new Date();
    const calendarItemDate = new Date(date);

    if (!strictMatch) {
        now.setHours(now.getHours(), 0, 0, 0); // we consider calendar item in past if it is less than start of current hour
    }

    return calendarItemDate < now;
}

export function isFutureCalendarItem(date: Date | string): boolean {
    return new Date(date) > new Date();
}

export function isActiveBooking(booking: Booking | null): boolean {
    if (!booking) {
        return false;
    }

    const now = new Date();
    const bookingFromDate = new Date(booking.booked_from);
    const bookingToDate = new Date(booking.booked_to);

    return bookingFromDate < now && bookingToDate > now;
}

export function formatBookingDate(date: Date) {
    return date
        .toLocaleTimeString(i18n.language, {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
        .replace(/\//g, '.');
}

export function getBookingDateRangeFromDatesDifference(
    daysDifference: number,
    hoursDifference: number,
    startDate: Date,
): [string, string] {
    const fromDate = startDate;
    const toDate = structuredClone(startDate);
    toDate.setHours(toDate.getHours() + 24 * daysDifference + hoursDifference);

    return [fromDate.toISOString(), toDate.toISOString()];
}

export function getBookingSelectionFromDaysAndHours(
    daysCount: number,
    hoursCount: number,
    startDate: Date,
    slotsIndexes?: SlotIndex[],
): BookingCalendarSelection {
    const dateRecord: Record<string, boolean> = {};
    const hoursToSelectCount = daysCount * 24 + hoursCount;

    for (let i = 0; i < hoursToSelectCount; i++) {
        const date = new Date(startDate);
        date.setHours(date.getHours() + i);

        let key;
        if (slotsIndexes !== undefined) {
            for (const slotIndex of slotsIndexes) {
                key = date.toISOString().concat(`_${slotIndex}`);
                dateRecord[key] = true;
            }
        } else {
            key = date.toISOString();
            dateRecord[key] = true;
        }
    }

    return dateRecord;
}

export function generateSlotsIndexes(slotsCount: number): SlotIndex[] {
    return [...Array(slotsCount).keys()].map((slotIndex) => slotIndex + 1);
}

export function generateSlotsIndexesRange(slotIndex: number, slotsCount: number): SlotIndex[] {
    return Array.from({ length: slotsCount }, (_, i) => slotIndex + i);
}

export function getNeighborBookingFirstSlotIndex(
    bookingObject: Pick<Booking, 'booked_from' | 'booked_to' | 'slots'> & { pk?: Booking['pk'] },
    bookings: Booking[],
): SlotIndex | null {
    function isDateRangeOverlap(
        aFrom: Date | string,
        aTo: Date | string,
        bFrom: Date | string,
        bTo: Date | string,
    ): boolean {
        return new Date(aFrom) < new Date(bTo) && new Date(bFrom) < new Date(aTo);
    }

    function hasSlotIntersection(slots1: SlotIndex[], slots2: SlotIndex[]): boolean {
        return slots1.some((slot) => slots2.includes(slot));
    }

    const bookingsWithIntersection = bookings.filter((booking) => {
        if (booking.pk === bookingObject.pk || !booking.slots || !bookingObject.slots) {
            return false;
        }

        return (
            isDateRangeOverlap(
                booking.booked_from,
                booking.booked_to,
                bookingObject.booked_from,
                bookingObject.booked_to,
            ) && hasSlotIntersection(booking.slots, bookingObject.slots)
        );
    });

    return bookingsWithIntersection.length
        ? Math.min(...bookingsWithIntersection.map((item) => item.slots as SlotIndex[]).flat())
        : null;
}

export function calculateActiveBookingStyles(
    borderColor: string,
    isFirstRow: boolean,
    isLastRow: boolean,
    isFirstCol: boolean,
    isLastCol: boolean,
): { rootStyle: SxProps; childStyle?: SxProps } | undefined {
    let rootStyle = {};
    let childStyle;

    const needOnlyTopBorder = isFirstRow && !isFirstCol && !isLastCol;
    const needOnlyBottomBorder = isLastRow && !isFirstCol && !isLastCol;
    const needOnlyLeftBorder = isFirstCol && !isFirstRow && !isLastRow;
    const needOnlyRightBorder = isLastCol && !isFirstRow && !isLastRow;
    const needTopAndLeftBorder = isFirstRow && isFirstCol;
    const needTopAndRightBorder = isFirstRow && isLastCol;
    const needBottomAndLeftBorder = isLastRow && isFirstCol;
    const needBottomAndRightBorder = isLastRow && isLastCol;
    const needsAllBorders = isFirstRow && isLastRow && isFirstCol && isLastCol;

    const sharedStyle = {
        content: '""',
        position: 'absolute',
        backgroundColor: borderColor,
        zIndex: 1,
    };

    const topBorderStyle = {
        '&::before': {
            ...sharedStyle,
            top: -1,
            left: -1,
            height: '4px',
            width: 'calc(100% + 2px)',
        },
    };

    const leftBorderStyle = {
        '&::after': {
            ...sharedStyle,
            top: -1,
            left: -1,
            height: 'calc(100% + 2px)',
            width: '4px',
        },
    };

    const rightBorderStyle = {
        '&::after': {
            ...sharedStyle,
            top: -1,
            right: -1,
            height: 'calc(100% + 2px)',
            width: '4px',
        },
    };

    const bottomBorderStyle = {
        '&::before': {
            ...sharedStyle,
            bottom: -1,
            left: -1,
            height: '4px',
            width: 'calc(100% + 2px)',
        },
    };

    if (needsAllBorders) {
        childStyle = {
            ...bottomBorderStyle,
            ...rightBorderStyle,
        };
    } else if (isFirstCol && isLastCol) {
        childStyle = rightBorderStyle;
    } else if (isFirstRow && isLastRow) {
        childStyle = bottomBorderStyle;
    }

    if (needOnlyTopBorder) {
        rootStyle = topBorderStyle;
    } else if (needOnlyBottomBorder) {
        rootStyle = bottomBorderStyle;
    } else if (needOnlyLeftBorder) {
        rootStyle = leftBorderStyle;
    } else if (needOnlyRightBorder) {
        rootStyle = rightBorderStyle;
    } else if (needTopAndLeftBorder) {
        rootStyle = {
            ...topBorderStyle,
            ...leftBorderStyle,
        };
    } else if (needTopAndRightBorder) {
        rootStyle = {
            ...topBorderStyle,
            ...rightBorderStyle,
        };
    } else if (needBottomAndLeftBorder) {
        rootStyle = {
            ...bottomBorderStyle,
            ...leftBorderStyle,
        };
    } else if (needBottomAndRightBorder) {
        rootStyle = {
            ...bottomBorderStyle,
            ...rightBorderStyle,
        };
    }

    return {
        rootStyle,
        childStyle,
    };
}

export function hasMinimumRequiredBookingDuration(date1: Date, date2: Date): boolean {
    const MIN_BOOKING_REQUIRED_TIME = 15; // in minutes

    const diffMs = Math.abs(date2.getTime() - date1.getTime());
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes > MIN_BOOKING_REQUIRED_TIME;
}

export function canStartBookingFromFullHour(start: Date) {
    const now = dayjs();

    const nextFullHour =
        now.minute() > 0
            ? now.add(1, 'hour').minute(0).second(0).millisecond(0)
            : now.minute(0).second(0).millisecond(0);

    const startNormalized = dayjs(start).second(0).millisecond(0);

    const canStart = startNormalized.valueOf() >= nextFullHour.valueOf();

    return {
        canStart,
        nextFullHour,
    };
}
export function isBookingStartedAtCurrentHourOrBefore(bookingStartDate: Date) {
    const start = dayjs(bookingStartDate);
    const now = dayjs();

    return start.isSame(now, 'hour') || start.isBefore(now, 'hour');
}

export function hasMinimumHourDuration(start: Date, end: Date, hourDuration: number): boolean {
    const HOURS_IN_MS = hourDuration * 60 * 60 * 1000;
    const diffMs = end.getTime() - start.getTime();

    return diffMs >= HOURS_IN_MS;
}

export function containsRange(a: [Date, Date], b: [Date, Date]): { leftMatches: boolean; rightMatches: boolean } {
    const [aStart, aEnd] = a;
    const [bStart, bEnd] = b;

    const leftMatches = aStart <= bStart;
    const rightMatches = aEnd >= bEnd;
    return { leftMatches, rightMatches };
}

export const findClosestBooking = (dateTime: string | Date, allBookings: Booking[]): Booking | null => {
    const localBookingsClone = structuredClone(allBookings);

    localBookingsClone.sort((a, b) => {
        const dateA = new Date(a.booked_from).getTime();
        const dateB = new Date(b.booked_from).getTime();
        return dateA - dateB;
    });

    return (
        allBookings.find((booking) => new Date(booking.booked_from).getTime() > new Date(dateTime).getTime()) || null
    );
};

export const useIsBookingSelected = ({
    bookingForItem,
    selectedDevice,
}: {
    bookingForItem: Booking | null;
    selectedDevice?: Device;
}): boolean => {
    const selectedBookings = useSelector(selectSelectedBookings);

    return useMemo(() => {
        if (!selectedDevice?.pk || !selectedBookings[selectedDevice.pk] || !bookingForItem) {
            return false;
        }

        return selectedBookings[selectedDevice.pk].pk === bookingForItem.pk;
    }, [bookingForItem, selectedBookings, selectedDevice?.pk]);
};

export function transformBookingDevicesResponse(response: AxiosResponse<DevicesResponse>): DevicesResponse {
    return {
        dut: response.data.dut.map((device: Omit<Device, 'type'>) => ({ ...device, type: DevicesType.DUT })),
        ixia: response.data.ixia.map((device: Omit<Device, 'type'>) => ({
            ...device,
            type: DevicesType.IXIA,
        })),
        spirent: response.data.spirent.map((device: Omit<Device, 'type'>) => ({
            ...device,
            type: DevicesType.SPIRENT,
        })),
    };
}

export function formatBookingRange(bookingFrom: Date, bookingTo: Date): string {
    return `${bookingFrom.toLocaleTimeString(i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })} - ${bookingTo.toLocaleTimeString(i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })}`;
}

type BookingWithTimezone = Booking & { timezone: string };

type GetBookingForSendResponse =
    | { booking: BookingWithTimezone; booking_id?: never }
    | { booking_id: Booking['pk']; booking?: never };

export const getBookingForSend = (booking: Booking): GetBookingForSendResponse => {
    if (booking.isLocal === true) {
        return {
            booking: {
                ...booking,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        };
    } else {
        return {
            booking_id: booking.pk,
        };
    }
};

export function credentialsExist(
    credentialsObject: CredentialsType,
): credentialsObject is { entry_point: string; password: string } {
    return (
        typeof credentialsObject.entry_point === 'string' &&
        credentialsObject.entry_point.trim().length > 0 &&
        typeof credentialsObject.password === 'string' &&
        credentialsObject.password.trim().length > 0
    );
}

export function getCredentials(
    device: DeviceForCredentials,
    credentialsObject: CredentialsType,
): (DutBookingCredentials[] | TrafficGeneratorBookingCredentials) | undefined {
    if (!credentialsExist(credentialsObject)) {
        return;
    }

    if (device.type === DevicesType.DUT) {
        const loginInfo = credentialsObject.entry_point.split('\n');
        const arr = loginInfo.map((loginInfoItem) => {
            const [label, command] = loginInfoItem.split(': ');
            return {
                label,
                command,
                password: credentialsObject.password,
            };
        });

        if (device.architecture === 'x86') {
            // Make IPMI element last
            const firstElement = arr.shift();
            firstElement && arr.push(firstElement);
        }

        return arr;
    } else {
        const [linkRaw, loginRaw] = credentialsObject.entry_point.split('\n');
        const link = linkRaw.split(': ')[1];
        const login = loginRaw.split(': ')[1];

        return {
            link,
            login,
            password: credentialsObject.password,
        };
    }
}
